import { Component, OnInit, OnDestroy, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IonBadge, IonSelect, IonSelectOption, AlertController } from '@ionic/angular/standalone';
import { MenuService } from '@app/core/services/menu.service';
import { AuthService } from '@app/core/services/auth.service';
import { LanguageService } from '@app/core/services/language.service';
import { ContextService, Client, Subsidiary, WorkingContext } from '@app/core/services/context.service';
import { MenuConfig, MenuItem } from '@app/core/models/menu.model';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon/dynamic-icon.component';
import { Observable, combineLatest } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { SocketService } from '@app/core/services/socket.service';
import { ReminderService } from '@app/core/services/reminder.service';
import { SubSink } from 'subsink';

/**
 * Context option for combined client/subsidiary selection
 */
interface ContextOption {
  value: string;  // Format: "clientId|subsidiaryId" or just "subsidiaryId"
  label: string;  // Display text
  clientId: string;
  subsidiaryId: string;
}

@Component({
  selector: 'app-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    DynamicIconComponent,
    IonBadge,
    IonSelect,
    IonSelectOption
  ]
})
export class NavigationMenuComponent implements OnInit, OnDestroy {
  private menuService = inject(MenuService);
  public authService = inject(AuthService);
  private contextService = inject(ContextService);
  private alertController = inject(AlertController);
  private languageService = inject(LanguageService);
  private socketService = inject(SocketService);
  private reminderService = inject(ReminderService);
  private router = inject(Router);
  private subsink = new SubSink();

  private currentMenuConfig: MenuConfig | null = null;
  
  menuConfig$!: Observable<MenuConfig>;
  isCollapsed$ = this.menuService.isCollapsed$;
  expandedSections: { [key: string]: boolean } = {};
  expandedItems: { [key: string]: boolean } = {};
  isMobile = false;
  mobileMenuOpen = false;
  contextMenuOpen = false;
  currentRouteLabel = '';
  private collapseTimer: any = null;
  dropdownLeft: number = 0;
  
  // Context selection
  context$!: Observable<WorkingContext>;
  availableClients$!: Observable<Client[]>;
  availableSubsidiaries$!: Observable<Subsidiary[]>;
  contextOptions$!: Observable<ContextOption[]>;
  isSuperAdmin = false;
  public showClientSubsidiarySelection:boolean = true;
  public dueRemindersCount: number = -1; // -1 indicates loading/error, 0 means no due reminders

  constructor() {
  }

  ngOnInit() {
    // Subscribe to user changes and reload menu when user changes
    this.menuConfig$ = this.authService.currentUser$.pipe(
      switchMap(() => this.refreshReminders()),
      switchMap(() => this.menuService.getMenu()),
      tap(config => {
        this.currentMenuConfig = config;
      })
    );
    
    // Listen to route changes to update active section
    this.subsink.sink = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // hide client/subsidiary selection if route is within a page detail
        this.showClientSubsidiarySelection = event.urlAfterRedirects.split('/').filter(segment => segment.length > 0).length < 2;
        // Update current route label for mobile display
        this.updateCurrentRouteLabel(event.urlAfterRedirects);
      }
    });
    
    // Initialize context observables
    this.context$ = this.contextService.getContext$();
    this.availableClients$ = this.contextService.getAvailableClients$();
    this.availableSubsidiaries$ = this.contextService.getAvailableSubsidiaries$();
    
    // Build context options from available clients and subsidiaries
    // Check isSuperAdmin dynamically each time to ensure it's current
    this.contextOptions$ = combineLatest([
      this.availableClients$,
      this.availableSubsidiaries$,
      this.authService.currentUser$
    ]).pipe(
      map(([clients, subsidiaries, user]) => {
        // Update isSuperAdmin flag when user changes
        this.isSuperAdmin = this.contextService.isSuperAdmin();
        return this.buildContextOptions(clients, subsidiaries);
      })
    );
    
    this.subsink.sink = this.socketService.on(`reminders-updated`).subscribe(async (notification: any) => {
      console.log(`Real-time reminders update received:`, notification);
      this.refreshReminders(); // Refresh due reminders count on update
    });
    this.subsink.sink = this.socketService.on(`reminders-created`).subscribe(async (notification: any) => {
      console.log(`Real-time reminders created received:`, notification);
      this.refreshReminders(); // Refresh due reminders count on creation
    });
    this.refreshReminders(); // Initial fetch of due reminders count
    this.checkScreenSize();
  }

  /**
   * Handle combined context selection change
   */
  onContextChange(event: any): void {
    const value = event.detail.value;
    if (!value) return;
    
    // Parse value: "clientId|subsidiaryId" for super-admin or just "subsidiaryId" for regular users
    const parts = value.split('|');
    
    if (parts.length === 2) {
      // Super-admin selecting client + subsidiary
      const [clientId, subsidiaryId] = parts;
      this.contextService.setClientAndSubsidiary(clientId, subsidiaryId);
    } else {
      // Regular user selecting just subsidiary
      this.contextService.setSubsidiary(value);
    }
  }

  /**
   * Get current context value for select
   */
  getContextValue(): string {
    const context = this.contextService.getCurrentContext();
    if (!context.subsidiaryId) return '';
    
    if (this.isSuperAdmin && context.clientId) {
      return `${context.clientId}|${context.subsidiaryId}`;
    }
    return context.subsidiaryId;
  }

  /**
   * Get placeholder text for context select
   */
  getContextPlaceholder(): string {
    return this.isSuperAdmin ? 'CONTEXT.SELECT_CLIENT_SUBSIDIARY' : 'CONTEXT.SELECT_SUBSIDIARY';
  }

  /**
   * Build context options from clients and subsidiaries
   * For super-admin: Group subsidiaries under their clients
   * For regular users: Just list available subsidiaries
   */
  private buildContextOptions(clients: Client[], subsidiaries: Subsidiary[]): ContextOption[] {
    // console.log('Building context options:', {
    //   isSuperAdmin: this.isSuperAdmin,
    //   clientsCount: clients.length,
    //   subsidiariesCount: subsidiaries.length
    // });
    
    const options: ContextOption[] = [];
    
    if (this.isSuperAdmin && clients.length > 0) {
      // Super-admin: create "Client > Subsidiary" options
      // Sort clients by name for better UX
      const sortedClients = [...clients].sort((a, b) => a.name.localeCompare(b.name));
      
      for (const client of sortedClients) {
        // Get subsidiaries for this client, sorted by name
        const clientSubs = subsidiaries
          .filter(sub => sub.clientId === client.clientId)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        // console.log(`Client ${client.name}: ${clientSubs.length} subsidiaries`);
        
        // Add an option for each subsidiary
        // If only one subsidiary, show just client name to avoid "APSYS > APSYS"
        const showSubsidiaryName = clientSubs.length > 1;
        
        for (const subsidiary of clientSubs) {
          options.push({
            value: `${client.clientId}|${subsidiary.subsidiaryId}`,
            label: showSubsidiaryName ? `${client.name} > ${subsidiary.name}` : client.name,
            clientId: client.clientId,
            subsidiaryId: subsidiary.subsidiaryId
          });
        }
      }
    } else {
      // Regular user: just list subsidiaries sorted by name
      const sortedSubs = [...subsidiaries].sort((a, b) => a.name.localeCompare(b.name));
      
      for (const subsidiary of sortedSubs) {
        options.push({
          value: subsidiary.subsidiaryId,
          label: subsidiary.name,
          clientId: subsidiary.clientId,
          subsidiaryId: subsidiary.subsidiaryId
        });
      }
    }
    
    // console.log('Built context options:', options.length);
    return options;
  }

  ngOnDestroy() {
    this.cancelCollapseTimer();
    this.closeMobileMenu(); // Clean up body overflow style
    this.closeContextMenu(); // Clean up context menu
    this.subsink.unsubscribe();
  }

  private async refreshReminders() {
    try {
      // Call an API to get the count of due reminders for the current user
      // This is a placeholder - replace with actual API call
      const userId = this.authService.currentUserValue?.id;
      console.log(`Fetching due reminders count for user ${userId}...`);
      if (!userId) {
        this.dueRemindersCount = -1; // Indicate error or not logged in
        return;
      }
      const res = await this.reminderService.getDueRemindersCount(userId!).toPromise();
      if (res && res.data) {
        this.dueRemindersCount = res.data.dueCount;
      } else {
        this.dueRemindersCount = -1;
      }
    } catch (error) {
      console.error('Error fetching due reminders count:', error);
      this.dueRemindersCount = -1;
    }
  }


  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  /**
   * Toggle mobile menu open/closed
   */
  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    // Prevent body scroll when menu is open
    if (this.mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  /**
   * Toggle context menu open/closed
   */
  toggleContextMenu(): void {
    this.contextMenuOpen = !this.contextMenuOpen;
    // Prevent body scroll when menu is open
    if (this.contextMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  /**
   * Close context menu
   */
  closeContextMenu(): void {
    this.contextMenuOpen = false;
    document.body.style.overflow = '';
  }

  /**
   * Update current route label for mobile display
   */
  private updateCurrentRouteLabel(url: string): void {
    if (!this.currentMenuConfig) {
      this.currentRouteLabel = '';
      return;
    }

    // Find the matching menu item in the config
    for (const section of this.currentMenuConfig.sections) {
      for (const item of section.items) {
        // Check if this item's route matches
        if (item.route && url.startsWith(item.route)) {
          this.currentRouteLabel = this.getLabel(item.labelKey, item.labels);
          return;
        }

        // Check children if they exist
        if (item.children) {
          for (const child of item.children) {
            if (child.route && url.startsWith(child.route)) {
              this.currentRouteLabel = this.getLabel(child.labelKey, child.labels);
              return;
            }
          }
        }
      }
    }

    // If no match found, try to extract from URL
    const segments = url.split('/').filter(s => s.length > 0);
    if (segments.length > 0) {
      // Capitalize and format the first segment as fallback
      this.currentRouteLabel = segments[0].charAt(0).toUpperCase() + segments[0].slice(1).replace(/-/g, ' ');
    } else {
      this.currentRouteLabel = '';
    }
  }

  private checkScreenSize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;
    
    // Close mobile menu if screen size changes from mobile to desktop
    if (wasMobile && !this.isMobile) {
      if (this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
      if (this.contextMenuOpen) {
        this.closeContextMenu();
      }
    }
  }

  toggleCollapse() {
    this.menuService.toggleCollapse();
  }

  toggleSection(sectionId: string, event?: Event, element?: HTMLElement) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.expandedSections[sectionId]) {
      this.expandedSections[sectionId] = false;
      // Clear expanded items when section is collapsed
      this.expandedItems = {};
    } else {
      // Close other sections for cleaner UI
      this.expandedSections = {};
      this.expandedSections[sectionId] = true;
      this.expandedItems = {};
      // Calculate dropdown position
      if (element) {
        const rect = element.getBoundingClientRect();
        this.dropdownLeft = rect.left;
      }
    }
  }

  expandSection(sectionId: string, element?: HTMLElement) {
    this.cancelCollapseTimer();
    // Close other sections first
    this.expandedSections = {};
    this.expandedSections[sectionId] = true;
    // Calculate dropdown position
    if (element) {
      const rect = element.getBoundingClientRect();
      this.dropdownLeft = rect.left;
    }
  }

  collapseSection(sectionId: string) {
    this.expandedSections[sectionId] = false;
    // Also clear expanded items when section collapses
    this.expandedItems = {};
  }

  startCollapseTimer(sectionId: string) {
    this.collapseTimer = setTimeout(() => {
      this.collapseSection(sectionId);
    }, 200);
  }

  cancelCollapseTimer() {
    if (this.collapseTimer) {
      clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }
  }

  isExpandedSection(sectionId: string): boolean {
    return !!this.expandedSections[sectionId];
  }

  toggleSubmenu(itemId: string, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.expandedItems[itemId]) {
      this.expandedItems[itemId] = false;
    } else {
      // Close other expanded items for cleaner UI
      this.expandedItems = {};
      this.expandedItems[itemId] = true;
    }
  }

  isExpanded(itemId: string): boolean {
    return !!this.expandedItems[itemId];
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  /**
   * Get translated label with fallback to original label
   * This ensures we show the actual label even if translation hasn't loaded yet
   */
  getLabel(labelKey: string | undefined, originalLabels: { [lang: string]: string } | undefined): string {
    if (!labelKey) {
      // Fallback to current language label from original labels
      const currentLang = this.languageService.currentLanguage;
      return originalLabels?.[currentLang] || Object.values(originalLabels || {})[0] || '';
    }
    
    // Try to get translation
    const translated = this.languageService.instant(labelKey);
    
    // If translation key is returned unchanged, use fallback
    if (translated === labelKey && originalLabels) {
      const currentLang = this.languageService.currentLanguage;
      return originalLabels[currentLang] || Object.values(originalLabels)[0] || labelKey;
    }
    
    return translated;
  }

  /**
   * Handle logout action with confirmation
   */
  async onLogout() {
    const alert = await this.alertController.create({
      header: this.languageService.instant('LOGOUT.CONFIRM_TITLE'),
      message: this.languageService.instant('LOGOUT.CONFIRM_MESSAGE'),
      buttons: [
        {
          text: this.languageService.instant('LOGOUT.CANCEL'),
          role: 'cancel'
        },
        {
          text: this.languageService.instant('LOGOUT.CONFIRM'),
          role: 'destructive',
          handler: () => {
            // Context service will clear localStorage on logout
            this.authService.logout().subscribe();
          }
        }
      ]
    });

    await alert.present();
  }

  onItemClick(sectionId: string) {
    if (this.isMobile) {
      this.expandedSections[sectionId] = false;
    }
  }

  onSubmenuClick(sectionId: string, itemId: string) {
    if (this.isMobile) {
      this.expandedItems[itemId] = false;
      this.expandedSections[sectionId] = false;
    }
  }

  /**
   * Check if a section contains the currently active route
   */
  isSectionActive(sectionId: string): boolean {
    if (!this.currentMenuConfig) return false;
    
    const section = this.currentMenuConfig.sections.find(s => s.id === sectionId);
    if (!section) return false;
    
    const currentUrl = this.router.url;
    
    // Check if any item in this section matches the current route
    return section.items.some(item => {
      // Check item route
      if (item.route && this.isRouteActive(item.route, currentUrl)) {
        return true;
      }
      
      // Check children routes
      if (item.children) {
        return item.children.some(child => 
          child.route && this.isRouteActive(child.route, currentUrl)
        );
      }
      
      return false;
    });
  }

  /**
   * Check if a route is active
   */
  private isRouteActive(route: string, currentUrl: string): boolean {
    // Remove query params and fragments for comparison
    const cleanUrl = currentUrl.split('?')[0].split('#')[0];
    const cleanRoute = route.split('?')[0].split('#')[0];
    
    // Exact match or starts with route (for child routes)
    return cleanUrl === cleanRoute || cleanUrl.startsWith(cleanRoute + '/');
  }
}
